import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Tags,
  MessageSquare,
  Inbox,
  Settings,
  LogOut,
  Menu,
  X,
  Pencil,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/comments", label: "Comments", icon: MessageSquare },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on navigation
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open (mobile)
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!user || (user.role !== "admin" && user.role !== "editor")) {
    return <Navigate to="/admin/login" replace />;
  }

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = ({ variant = "desktop" }) => (
    <>
      <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between gap-2">
        <Link to="/admin/dashboard" className="flex items-center gap-2 min-w-0" data-testid={`admin-brand-${variant}`}>
          <div className="w-9 h-9 bg-slate-950 rounded-md flex items-center justify-center text-white font-heading font-bold text-sm flex-shrink-0">BD</div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-slate-950 text-sm truncate">BuildDirect</div>
            <div className="text-[10px] tracking-widest uppercase text-slate-500">Admin Panel</div>
          </div>
        </Link>
        {variant === "mobile" && (
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            data-testid="admin-drawer-close"
            className="-mr-1 p-2 text-slate-500 hover:text-slate-900 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`admin-nav-${n.label.toLowerCase()}-${variant}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-slate-950 text-white" : "text-slate-700 hover:bg-slate-100"
              }`
            }
          >
            <n.icon className="w-4 h-4 flex-shrink-0" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="text-[11px] text-slate-500 mb-2 truncate px-1">{user.email}</div>
        <button
          onClick={onLogout}
          data-testid={`admin-logout-btn-${variant}`}
          className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
        <Link
          to="/"
          data-testid={`admin-open-live-editor-${variant}`}
          className="w-full inline-flex items-center gap-2 px-3 py-2 mt-1 rounded-md text-sm text-white bg-emerald-600 hover:bg-emerald-500 font-medium"
        >
          <Pencil className="w-4 h-4" /> Open live editor
        </Link>
      </div>
    </>
  );

  return (
    <div className="admin-panel min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between gap-2 px-3 h-14" data-testid="admin-mobile-topbar">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          data-testid="admin-drawer-open"
          className="p-2 -ml-1 text-slate-700 hover:text-slate-950 rounded-md hover:bg-slate-100"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/admin/dashboard" className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 bg-slate-950 rounded-md flex items-center justify-center text-white font-heading font-bold text-[11px] flex-shrink-0">BD</div>
          <div className="font-heading font-semibold text-slate-950 text-sm truncate">Admin</div>
        </Link>
        <div className="w-9" />{/* spacer to balance the menu button */}
      </header>

      {/* Desktop sidebar (lg+) */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col fixed top-0 bottom-0 left-0 z-30">
        <SidebarContent variant="desktop" />
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          className="absolute inset-0 bg-slate-950/50"
          tabIndex={drawerOpen ? 0 : -1}
        />
        <aside
          className={`absolute top-0 left-0 bottom-0 w-72 max-w-[80vw] bg-white shadow-xl flex flex-col transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="admin-drawer"
        >
          <SidebarContent variant="mobile" />
        </aside>
      </div>

      {/* Main content area */}
      <main className="lg:ml-64 px-4 sm:px-6 lg:px-10 py-5 sm:py-7 lg:py-10 min-h-[calc(100vh-3.5rem)] lg:min-h-screen">
        <div className="max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
